import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, defer, from, map, mergeMap, Observable, of, throwError, toArray } from 'rxjs';
import { CartingService, IResponseWagon, IWagon } from '../carting.service';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema, IImportedData } from '../file-transfer-services/interfaces';
import { FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Wagons';
const fileName = 'Carting-wagons';
const importConcurrency = 5;

/** The only wagon types/layers the create/edit dialog offers - import rejects anything else the same way. */
const ALLOWED_TYPES = ['frame', 'sash'];
const ALLOWED_LAYERS = ['primary', 'coupled-b', 'coupled-c'];

const NAME_PATTERN = /^[0-9a-zA-ZÆØÅæøå\s-_]*$/;
const DESCRIPTION_PATTERN = /^[0-9a-zA-ZÆØÅæøå.,\s]*$/;

export interface IWagonExportData {
	name: string;
	description?: string;
	batchType?: string;
	cells: number;
	type: string;
	layer: string;
}

const WagonSchema: IFileDataSchema<IWagonExportData> = {
	name: sheetName,
	properties: {
		name: { type: 'string', required: true, fileKey: 'Name', width: 30 },
		description: { type: 'string', required: false, fileKey: 'Description', width: 50 },
		batchType: { type: 'string', required: false, fileKey: 'BatchType', width: 30 },
		cells: { type: 'number', required: true, fileKey: 'Cells', width: 15 },
		type: { type: 'string', required: true, fileKey: 'Type', width: 15 },
		layer: { type: 'string', required: true, fileKey: 'Layer', width: 20 },
	},
};

interface WagonImportContext {
	wagonNameMap: Map<string, IResponseWagon>;
}

/** A row that passed validation, carrying its file location through to a save failure too. */
interface WagonImportCandidate {
	wagon: IWagon;
	location?: string;
}

@Injectable({
	providedIn: 'root',
})
export class WagonTransferService implements ITransferService<IResponseWagon> {
	private translationPrefix = 'masterdata.carting.wagonImportExport';

	constructor(
		private cartingService: CartingService,
		private excelFileTransfer: ExcelFileTransfer<IWagonExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => true;
	public getTemplate = (): void => {
		this.downloadTemplate();
	};

	// Wagon name uniqueness is not enforced anywhere on the frontend - neither the create/edit
	// dialog nor this list page pre-check it, it is only rejected by the backend on save. Nothing
	// here treats name as unique on faith: both the file and the existing roster are checked.
	public exportData(wagons: IResponseWagon[]): Observable<IResponseWagon[]> {
		const duplicateNames = findDuplicateNames(wagons);
		if (duplicateNames.length > 0) {
			return throwError(
				() =>
					new Error(
						this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-names.content`, {
							names: duplicateNames.join(', '),
						}),
					),
			);
		}

		const data = wagons.map((wagon) => this.toExportRow(wagon));

		// ExcelFileTransfer.exportData writes the file (and validates cell lengths) synchronously
		// before returning its observable, so calling it directly here would let a real write
		// failure throw past catchError below instead of reaching it - defer() delays that call
		// until subscription, inside RxJS's own error handling.
		return defer(() => this.excelFileTransfer.exportData(fileName, data, WagonSchema)).pipe(
			map(() => wagons),
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	// A blank starting point for import: the same fields exportData produces, with one filled-in
	// example row instead of real data.
	public downloadTemplate(): void {
		const exampleRow: IWagonExportData = {
			name: 'Example Wagon',
			description: 'Example description',
			batchType: 'Example Batch Type',
			cells: 10,
			type: 'frame',
			layer: 'primary',
		};

		this.excelFileTransfer.exportData(`${fileName}Template`, [exampleRow], WagonSchema).subscribe();
	}

	private toExportRow(wagon: IResponseWagon): IWagonExportData {
		return {
			name: wagon.name,
			description: wagon.description || '',
			batchType: wagon.batchType?.self ? decodeURIComponent(this.cartingService.getLastSegment(wagon.batchType.self)) : undefined,
			cells: wagon.configuration.cells,
			type: this.cartingService.getLastSegment(wagon.configuration.type.self),
			layer: this.cartingService.getLastSegment(wagon.configuration.layer.self),
		};
	}

	// Duplicate names (file or roster) and per-row validation problems all get discovered and
	// reported together in one pass, instead of a user fixing one category, re-importing, and only
	// then discovering the next.
	public importData(file: File | File[]): Observable<ImportResponse> {
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			toArray(),
			mergeMap((parsedRows) => {
				// Nothing parsed - whatever went wrong is already in ImportErrors. Stop here instead
				// of fetching the roster only to report an unrelated duplicate-name error that would
				// bury the actual parse failure.
				if (parsedRows.length === 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const fileDuplicates = findDuplicateNames(parsedRows.map((imported) => imported.data));

				return this.cartingService.getWagons().pipe(
					mergeMap((wagons) => {
						const rosterDuplicates = findDuplicateNames(wagons.items);

						if (fileDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-file', fileDuplicates);
						}
						if (rosterDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-roster', rosterDuplicates);
						}
						const namesAreAmbiguous = fileDuplicates.length > 0 || rosterDuplicates.length > 0;

						const ctx: WagonImportContext = {
							wagonNameMap: buildWagonNameMap(wagons.items),
						};

						return from(parsedRows).pipe(
							filterMap(this.validateRow(ImportErrors)),
							toArray(),
							mergeMap((validRows) => (namesAreAmbiguous ? of([]) : from(validRows).pipe(mergeMap(this.createOrUpdateWagon(ctx, ImportErrors), importConcurrency), toArray()))),
							map(
								(results) =>
									({
										successfulImports: results.map(
											(r) =>
												({
													Id: r.entity.name,
													status: r.status,
												}) as SuccessFullImport,
										),
										errors: ImportErrors,
									}) as ImportResponse,
							),
						);
					}),
				);
			}),
		);
	}

	private importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IImportedData<IWagonExportData>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, WagonSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						message: e.error,
						status: ImportStatus.ParsingError,
					} as FailedParse);
				});

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	// Mirrors every validator the create/edit dialog applies to its form (name/description
	// patterns and lengths, the batch-type pattern and length, the cells range, and the closed
	// type/layer lists), since import is a second, dialog-bypassing entry point into the same data.
	private validateRow(ImportErrors: ImportErrorTypes[]) {
		return (imported: IImportedData<IWagonExportData>): WagonImportCandidate | undefined => {
			const { data: row, location } = imported;
			const errors: Record<string, ValidationErrors> = {};

			const name = (row.name ?? '').trim();
			if (!name) {
				errors['name'] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.missing-name`),
				};
			} else if (name.length > 50) {
				errors['name'] = {
					maxlength: this.translator.instant(`${this.translationPrefix}.import.errors.name-too-long`),
				};
			} else if (!NAME_PATTERN.test(name)) {
				errors['name'] = {
					pattern: this.translator.instant(`${this.translationPrefix}.import.errors.name-invalid-characters`),
				};
			}

			const description = (row.description ?? '').trim();
			if (description.length > 100) {
				errors['description'] = {
					maxlength: this.translator.instant(`${this.translationPrefix}.import.errors.description-too-long`),
				};
			} else if (description && !DESCRIPTION_PATTERN.test(description)) {
				errors['description'] = {
					pattern: this.translator.instant(`${this.translationPrefix}.import.errors.description-invalid-characters`),
				};
			}

			const batchType = (row.batchType ?? '').trim();
			if (batchType.length > 255) {
				errors['batchType'] = {
					maxlength: this.translator.instant(`${this.translationPrefix}.import.errors.batch-type-too-long`),
				};
			} else if (batchType && !NAME_PATTERN.test(batchType)) {
				errors['batchType'] = {
					pattern: this.translator.instant(`${this.translationPrefix}.import.errors.batch-type-invalid-characters`),
				};
			}

			this.validateRange(errors, 'cells', row.cells, 0, 10000, true);

			const type = (row.type ?? '').trim();
			if (!type) {
				errors['type'] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.missing-type`),
				};
			} else if (!ALLOWED_TYPES.includes(type)) {
				errors['type'] = {
					invalid: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-type`, { value: type }),
				};
			}

			const layer = (row.layer ?? '').trim();
			if (!layer) {
				errors['layer'] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.missing-layer`),
				};
			} else if (!ALLOWED_LAYERS.includes(layer)) {
				errors['layer'] = {
					invalid: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-layer`, { value: layer }),
				};
			}

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({
					Id: name || '',
					location,
					status: ImportStatus.ValidationError,
					errors,
				} as FailedValidationImport);
				return undefined;
			}

			const wagon: IWagon = {
				self: null,
				name,
				description: description || '',
				batchType: batchType || null,
				updatedDate: null,
				updatedBy: null,
				cells: row.cells,
				type: `/carting/wagon-types/${type}`,
				translationType: null,
				layer: `/carting/wagon-layers/${layer}`,
				translationLayer: null,
			};

			return { wagon, location };
		};
	}

	private validateRange(errors: Record<string, ValidationErrors>, field: string, value: number | undefined, min: number, max: number, required: boolean): void {
		if (value === undefined || value === null) {
			if (required) {
				errors[field] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKeyForField(field)}`, {
						min,
						max,
					}),
				};
			}
			return;
		}

		if (!Number.isFinite(value) || !Number.isInteger(value) || value < min || value > max) {
			errors[field] = {
				range: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKeyForField(field)}`, {
					min,
					max,
				}),
			};
		}
	}

	private createOrUpdateWagon(ctx: WagonImportContext, ImportErrors: ImportErrorTypes[]) {
		return (candidate: WagonImportCandidate) => {
			const { wagon, location } = candidate;
			// Safe: importData() has already confirmed neither the file nor the roster has a name
			// collision, so at most one existing wagon can match here.
			const existingWagon = ctx.wagonNameMap.get(normalizeName(wagon.name));

			if (existingWagon) {
				wagon.self = existingWagon.self;
				return this.cartingService.updateWagon(wagon).pipe(
					map(() => ({ entity: wagon, status: ImportStatus.SuccessUpdate })),
					catchError((error) => {
						this.pushSaveFailedError(ImportErrors, wagon, location, error);
						return of();
					}),
				);
			}

			return this.cartingService.createWagon(wagon).pipe(
				map(() => ({ entity: wagon, status: ImportStatus.SuccessCreate })),
				catchError((error) => {
					this.pushSaveFailedError(ImportErrors, wagon, location, error);
					return of();
				}),
			);
		};
	}

	// Never surface the backend's raw error text - map it to a translated, user-facing message
	// instead. Anything we haven't special-cased falls back to a generic "failed to save" message.
	private saveFailedErrorKey(error: unknown): string {
		switch ((error as HttpErrorResponse)?.status) {
			case 400:
				return 'save-failed-bad-request';
			case 404:
				return 'save-failed-not-found';
			default:
				return 'save-failed';
		}
	}

	private pushSaveFailedError(ImportErrors: ImportErrorTypes[], wagon: IWagon, location: string | undefined, error: unknown) {
		ImportErrors.push({
			Id: wagon.name,
			location,
			status: ImportStatus.Failed,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${this.saveFailedErrorKey(error)}`, {
				name: wagon.name,
			}),
		});
	}

	private pushDuplicateNamesError(ImportErrors: ImportErrorTypes[], errorKey: 'duplicate-names-in-file' | 'duplicate-names-in-roster', names: string[]): void {
		ImportErrors.push({
			status: ImportStatus.ParsingError,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKey}`, {
				names: names.join(', '),
			}),
		} as FailedParse);
	}
}

function errorKeyForField(field: string): string {
	return `invalid-${field}`;
}

function buildWagonNameMap(wagons: IResponseWagon[]): Map<string, IResponseWagon> {
	const map = new Map<string, IResponseWagon>();
	wagons.forEach((wagon) => map.set(normalizeName(wagon.name), wagon));
	return map;
}

/** Names that appear 2+ times once trimmed/lowercased. Blank names are skipped - a missing name is reported separately as a missing-required-field error, not a collision. Returns the original (non-normalized) spelling of every row involved in a collision, for a message a user can act on. */
function findDuplicateNames(items: { name: string }[]): string[] {
	const firstSeen = new Map<string, string>();
	const duplicates = new Set<string>();

	for (const item of items) {
		const normalized = normalizeName(item.name);
		if (!normalized) {
			continue;
		}

		const original = firstSeen.get(normalized);
		if (original !== undefined) {
			duplicates.add(original);
			duplicates.add(item.name);
		} else {
			firstSeen.set(normalized, item.name);
		}
	}

	return Array.from(duplicates);
}

function normalizeName(name: unknown): string {
	return name === null || name === undefined ? '' : String(name).trim().toLowerCase();
}
